import React from "react";
import { mediaUrl } from "@/lib/api";
import { PAYMENT_METHODS, PAYMENT_STATUSES } from "@/lib/printTemplates";

const n2 = (v) => Number(v || 0).toFixed(2);

const E = ({ edit, path, children, style }) => edit ? (
  <span contentEditable suppressContentEditableWarning data-edit-path={path}
    onBlur={(e) => edit(path, e.currentTarget.innerText)}
    style={{ outline: "1px dashed rgba(197,160,89,.8)", outlineOffset: 1, cursor: "text", ...style }}>{children}</span>
) : <span style={style}>{children}</span>;

const Party = ({ title, p, L, edit, lp }) => (
  <div style={{ flex: 1 }}>
    <div style={{ fontSize: "0.8em", textTransform: "uppercase", letterSpacing: 1, opacity: 0.7 }}><E edit={edit} path={`labels.${lp}`}>{title}</E></div>
    <div style={{ fontWeight: 700, fontSize: "1.1em", marginTop: 2 }}>{p.legal_name || p.company || p.name || "—"}</div>
    {p.company && p.name && p.company !== p.name && <div>{p.name}</div>}
    {[["eik", p.eik], ["vat_number", p.vat_number], ["address", p.address], ["mol", p.mol], ["phone", p.phone], ["email", p.email], ["bank", p.bank], ["iban", p.iban]]
      .filter(([, v]) => v).map(([k, v]) => <div key={k}><E edit={edit} path={`labels.${k}`}>{L[k]}</E>: {v}</div>)}
  </div>
);

const cell = { padding: "2mm 1.5mm", borderBottom: "1px solid #e5e7eb", textAlign: "left" };
const colVal = (c, ln, i) => ({
  idx: i + 1, description: ln.description, quantity: ln.quantity, unit: ln.unit, unit_price: n2(ln.unit_price),
  vat_rate: `${ln.vat_rate}%`, base: n2(ln.base), vat: n2(ln.vat), total: n2(ln.total),
}[c.key]);

function Block({ id, tpl, inv, edit }) {
  const L = tpl.labels, gap = `${tpl.spacing_mm}mm`;
  const box = { marginBottom: gap };
  switch (id) {
    case "header":
      return (
        <div style={{ ...box, display: "flex", alignItems: "flex-start", gap: "6mm", flexDirection: tpl.logo_align === "right" ? "row-reverse" : "row" }}>
          {tpl.logo_url ? <img src={mediaUrl(tpl.logo_url)} alt="" style={{ width: `${tpl.logo_mm}mm`, objectFit: "contain" }} /> : <div />}
          <div style={{ flex: 1, textAlign: tpl.logo_align === "right" ? "left" : "right" }}>
            <div style={{ fontSize: "2.2em", fontWeight: 700, color: tpl.accent, letterSpacing: 2 }}><E edit={edit} path="title">{tpl.title}</E></div>
            <div style={{ fontWeight: 600 }}><E edit={edit} path="labels.number">{L.number}</E>: {inv.number_str}</div>
            <div><E edit={edit} path="labels.date">{L.date}</E>: {inv.issue_date}</div>
            <div style={{ fontSize: "0.85em", marginTop: 2 }}><E edit={edit} path="original_label">{tpl.original_label}</E></div>
            {(tpl.header_note || edit) && <div style={{ fontSize: "0.85em", marginTop: 2, whiteSpace: "pre-line" }}><E edit={edit} path="header_note">{tpl.header_note || "…"}</E></div>}
            {inv.status === "CANCELLED" && <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: "1.3em" }}>{L.cancelled}</div>}
          </div>
        </div>
      );
    case "parties":
      return (
        <div style={{ ...box, display: "flex", gap: "8mm", padding: "4mm", border: `1px solid ${tpl.accent}33`, borderRadius: 4 }}>
          <Party title={L.supplier} lp="supplier" p={inv.company || {}} L={L} edit={edit} />
          <Party title={L.recipient} lp="recipient" p={inv.customer || {}} L={L} edit={edit} />
        </div>
      );
    case "stay":
      if (!inv.stay) return null;
      return (
        <div style={{ ...box, display: "flex", gap: "8mm", flexWrap: "wrap" }}>
          <div><E edit={edit} path="labels.arrival">{L.arrival}</E>: <b>{inv.stay.arrival}</b></div>
          <div><E edit={edit} path="labels.departure">{L.departure}</E>: <b>{inv.stay.departure}</b></div>
          <div><E edit={edit} path="labels.room">{L.room}</E>: <b>{inv.stay.room_number} {inv.stay.room_name}</b></div>
          <div><E edit={edit} path="labels.nights">{L.nights}</E>: <b>{inv.stay.nights}</b></div>
        </div>
      );
    case "lines": {
      const cols = tpl.columns.filter((c) => c.visible);
      return (
        <table style={{ ...box, width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: tpl.accent, color: "#fff" }}>
            {cols.map((c) => <th key={c.key} style={{ ...cell, borderBottom: 0, textAlign: ["description", "unit"].includes(c.key) ? "left" : "right" }}>
              <E edit={edit && ((p, v) => edit(`columns.${c.key}`, v))} path={`col.${c.key}`}>{c.label}</E></th>)}
          </tr></thead>
          <tbody>{(inv.lines || []).map((ln, i) => (
            <tr key={i}>{cols.map((c) => <td key={c.key} style={{ ...cell, textAlign: ["description", "unit"].includes(c.key) ? "left" : "right" }}>{colVal(c, ln, i)}</td>)}</tr>
          ))}</tbody>
        </table>
      );
    }
    case "totals":
      return (
        <div style={{ ...box, marginLeft: "auto", width: "75mm" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><E edit={edit} path="labels.base">{L.base}</E><span>{n2(inv.totals?.base)} €</span></div>
          {(inv.totals?.vat_groups || []).map((g) => (
            <div key={g.rate} style={{ display: "flex", justifyContent: "space-between" }}><span>{L.vat} {g.rate}%</span><span>{n2(g.vat)} €</span></div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.25em", borderTop: `2px solid ${tpl.accent}`, marginTop: "2mm", paddingTop: "2mm", color: tpl.accent }}>
            <E edit={edit} path="labels.total">{L.total}</E><span>{n2(inv.totals?.total)} €</span>
          </div>
        </div>
      );
    case "payment":
      return (
        <div style={{ ...box, display: "flex", gap: "8mm" }}>
          <div><E edit={edit} path="labels.payment_method">{L.payment_method}</E>: <b>{PAYMENT_METHODS[inv.payment_method] || inv.payment_method || "—"}</b></div>
          <div><E edit={edit} path="labels.payment_status">{L.payment_status}</E>: <b>{PAYMENT_STATUSES[inv.payment_status] || inv.payment_status || "—"}</b></div>
        </div>
      );
    case "notes":
      if (!inv.notes && !edit) return null;
      return <div style={{ ...box, whiteSpace: "pre-line" }}><b><E edit={edit} path="labels.notes">{L.notes}</E>:</b> {inv.notes || "—"}</div>;
    case "signatures":
      return (
        <div style={{ ...box, display: "flex", justifyContent: "space-between", marginTop: "12mm" }}>
          {["compiled", "received"].map((k) => (
            <div key={k} style={{ width: "60mm", borderTop: "1px solid #999", paddingTop: "1mm", textAlign: "center", fontSize: "0.9em" }}>
              <E edit={edit} path={`labels.${k}`}>{L[k]}</E>
            </div>
          ))}
        </div>
      );
    case "footer":
      if (!tpl.footer_text && !edit) return null;
      return <div style={{ ...box, borderTop: "1px solid #e5e7eb", paddingTop: "2mm", fontSize: "0.85em", textAlign: "center", whiteSpace: "pre-line" }}><E edit={edit} path="footer_text">{tpl.footer_text || "…"}</E></div>;
    default:
      return null;
  }
}

export function InvoiceDoc({ tpl, inv, edit, selected, onSelect }) {
  const m = tpl.margins_mm;
  return (
    <div style={{ width: "210mm", minHeight: "297mm", padding: `${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm`, background: "#fff",
      color: tpl.text_color, fontFamily: `${tpl.font_family}, sans-serif`, fontSize: `${tpl.font_size}pt`, lineHeight: 1.4 }}>
      {tpl.blocks.filter((b) => !tpl.hidden.includes(b)).map((b) => (
        <div key={b} onClick={onSelect ? () => onSelect(b) : undefined}
          style={onSelect ? { outline: selected === b ? "2px solid #C5A059" : "1px dashed transparent", cursor: "pointer" } : undefined}>
          <Block id={b} tpl={tpl} inv={inv} edit={edit} />
        </div>
      ))}
    </div>
  );
}

export const SAMPLE_INVOICE = (company) => ({
  number_str: "0000000001", issue_date: new Date().toISOString().slice(0, 10), status: "ISSUED", company: company || {},
  customer: { name: "Иван Петров", company: "Примерна Фирма ЕООД", eik: "000000000", address: "гр. София" },
  stay: { arrival: "2026-07-01", departure: "2026-07-04", nights: 3, room_number: "101", room_name: "Стандартна стая" },
  lines: [{ description: "Нощувка – стая 101", quantity: 3, unit: "нощ.", unit_price: 60, vat_rate: 9, base: 165.14, vat: 14.86, total: 180 }],
  totals: { base: 165.14, vat: 14.86, total: 180, vat_groups: [{ rate: 9, base: 165.14, vat: 14.86 }] },
  payment_method: "CASH", payment_status: "PAID", notes: "Примерна фактура за преглед",
});
